/**
 * Finding one statistic among several hundred.
 *
 * The Statistic cell used to be a bare `<select>`, and the number of options in
 * it is the whole of the problem. Counted against the live dictionary on
 * 2026-09-29:
 *
 * | sport      | entries |
 * |------------|---------|
 * | football   |     392 |
 * | soccer     |     200 |
 * | hockey     |     141 |
 * | lacrosse   |      38 |
 * | basketball |      17 |
 *
 * Scoping the list to the profile's sport (`client-feedback-round-2/03`) was
 * the right change and it did not touch this: a football source still opens a
 * menu of 392 rows, in dictionary order, with no way through it but the scroll
 * bar and the browser's own type-ahead — which matches from the *start* of the
 * label only, so a reviewer looking for "Yards After Catch" has to know that
 * the dictionary files it under Y rather than under Receiving. A thirteen-report
 * PFF source is a hundred-odd of these decisions in a row.
 *
 * So the options are matched against a typed term here, and the cell renders a
 * listbox instead. **What that listbox prints about the row under the cursor is
 * decided here too** — `statDetail`, and `StatDetail` for what it holds — for
 * the same reason the matching is: a rule written inside the JSX is a rule
 * nothing can assert.
 *
 * Everything in this module is a string operation over data the
 * screen already has; it holds no React and no DOM, which is what lets
 * `tests/stat-search.test.ts` run it under bare `node`
 * (`client-feedback-round-2/04`).
 *
 * **What is searched is what is printed.** Every option's searchable text is
 * built from its own label, plus the key it stores and the aliases it answers
 * to. That is a rule about trust rather than a convenience: a row that appears
 * for a reason the reviewer cannot see on it is a row they have to guess about,
 * and the one thing a search over a Stat Dictionary must never do is put an
 * unexplained statistic under the cursor — `stat_key` decides a published
 * column's name and its Direction, and picking the wrong one ranks a Cohort
 * backwards with nothing raising (ADR-0017).
 */

import type { StatEntry } from "./api";

/**
 * One labelled fact in the detail panel: `Key`, `Sport`, `Default weight`,
 * `Derived`, `Distribution`.
 *
 * A list rather than a typed record, because what the panel prints is decided
 * here and a fact nobody declared is **absent** rather than present and blank.
 * Those are different claims about an entry — a missing `Default weight` means
 * nothing was filed, which weighs 1, and is not the same review state as a
 * declared 1 — and a row of em dashes under five headings says neither.
 */
export interface StatFact {
  label: string;
  value: string;
}

/** One excluded group, with what feeds it. Labels may be empty — `UNKNOWN` is
 *  excludable and nothing maps to it. */
export interface ExcludedGroup {
  group: string;
  labels: readonly string[];
}

/**
 * What the menu shows about the row under the cursor.
 *
 * **Every field here was already in the browser and was being thrown away.**
 * `/stat-dictionary` serves the Direction, the aliases, the default weight, the
 * distribution and the per-90 pair on the list endpoint, and the menu printed a
 * display name. Reaching any of them meant the Stat Dictionary screen, which is
 * a different route, and both field-mapping screens hold an unsaved review in
 * client state — so going to look **discarded the configuration**
 * (`field-mapping-usability/02`).
 *
 * **The `description` is served and deliberately not shown.** It was, in the
 * first cut, and the client asked for it out on reading one. Counted on the
 * live dictionary: 290 of football's 321 descriptions are the import's
 * provenance note — "PFF passing_summary.csv.", from `metricNamesFBS.csv`'s
 * `file_names` column, plus "Client-selected for scoring." where its `select`
 * column was ticked — rather than an account of what the statistic is. Two
 * structured facts in a prose field, because the entry has nowhere else to put
 * them, and nothing anywhere reads them back.
 *
 * That it also drops the 31 football entries, the 82 soccer and the 38 lacrosse
 * ones that *do* explain themselves is the cost, and it was the client's call
 * with the trade stated. The entry is one `description` field; a panel cannot
 * show the explaining ones and hide the provenance ones without sniffing prose
 * for a filename, which would break the moment somebody edited one.
 */
export interface StatDetail {
  /**
   * Which way this statistic ranks, in words, **both ways**.
   *
   * The half of the request that was already half-answered. A label carries
   * "(lower is better)" and nothing says the opposite, so 69 of football's 403
   * live entries declare a Direction and the other 334 declare an absence —
   * and an absence is what sent the client to the dictionary to check. A reader
   * cannot tell "ranks higher-is-better" from "nobody has looked at this yet"
   * by reading nothing.
   *
   * **Null where the menu's choice is not ranked at all**, which is the
   * eligibility rule on `SourceDetail`. `_is_eligible` is `value >= minimum`
   * and never reads a Direction, so stating one there would describe machinery
   * that does not run on that screen's decision.
   */
  direction: string | null;
  /** The same fact as the panel's arrow reads it. Meaningless where `direction`
   *  is null, and the panel draws neither. */
  higherIsBetter: boolean;
  /**
   * Why the Direction above is not the one the entry declares, where that is
   * the case — which is the out-of-sport row and nothing else.
   *
   * Null on every ordinary row, because a sentence saying nothing is wrong,
   * under each of 403 rows, is noise.
   */
  directionNote: string | null;
  facts: readonly StatFact[];
  /**
   * The Canonical Position Groups this statistic does not rank, each with the
   * publisher labels that feed it.
   *
   * **A group name alone does not say who it is.** The menu is where a reviewer
   * decides which statistic to bind, and a statistic that declines to rank half
   * the squad is a different choice from one that ranks everybody — but
   * "excludes Special teams" only answers that for somebody who already knows
   * what is in *Special teams*. The labels are the cohort.
   *
   * Empty where the entry excludes none, and empty on a `threshold` menu: an
   * exclusion takes a statistic out of a player's Impact Factor, and the
   * eligibility rule never ranks anything, so stating one there would describe
   * machinery that does not run on that screen's decision — the rule the rest
   * of `rankingFacts` already keeps.
   */
  excluded: readonly ExcludedGroup[];
  /**
   * What this row can also be found by.
   *
   * `searchable` matches on aliases, and this module calls that its one
   * departure from *what is searched is what is printed*. Printing them here
   * closes it: a row reached by typing `QBR` now says that `QBR` is why it came
   * back.
   */
  aliases: readonly string[];
}

/**
 * What a Run does with the statistic this menu chooses, which decides which of
 * the entry's declarations are worth previewing.
 *
 * `"rank"` is both field-mapping screens: the choice becomes a published column
 * and the ranker reads its Direction, its Distribution and its per-90
 * derivation. `"threshold"` is the eligibility rule on `SourceDetail`, where
 * `_is_eligible` is `value >= minimum` and reads **none** of them — so a panel
 * that printed a Direction there would be describing machinery that does not
 * run on that screen's decision.
 */
export type StatUse = "rank" | "threshold";

/** The stored four-character stubs as words. The dictionary screen prints the
 *  stub, so that screen reads "gamm"; four characters is a storage decision. */
const DISTRIBUTIONS: Record<string, string> = {
  beta: "Beta",
  gamm: "Gamma",
  gaus: "Gaussian",
};

function directionWords(direction: string): string {
  return direction === "lower_is_better" ? "Lower is better" : "Higher is better";
}

/**
 * Which entry this is: the key that gets stored and the vocabulary it was read
 * from. True on every row, whatever the screen does with the choice.
 */
function identityFacts(entry: StatEntry): StatFact[] {
  return [
    { label: "Key", value: entry.key },
    { label: "Sport", value: entry.sport },
  ];
}

/**
 * The declarations only a *ranked* choice consumes, and only when the entry is
 * the one the Run will resolve.
 *
 * **Kept apart from the identity facts because they are the ones that can be
 * true of the entry and false of the column.** Every one of them is looked up
 * by the profile's sport and the key together — `context.directions`,
 * `context.distributions` and `context.scales` in `runner.py` are all built
 * from `StatDictionaryEntry.sport == profile.sport` — so on a row whose key
 * that sport does not hold, none of them reaches the Run. Printing a soccer
 * entry's "Distribution Beta" beside a football profile's column is the same
 * mistake as printing its Direction, which `offDictionaryDetail` exists to
 * refuse; it was caught by review on the first cut of this panel, where the
 * Direction was corrected and these were not.
 *
 * The default weight is here for a different reason and the same effect: it is
 * what *discovery* copies onto a new Binding, and discovery matches within the
 * profile's sport too.
 */
function rankingFacts(entry: StatEntry): StatFact[] {
  const facts: StatFact[] = [];
  if (entry.default_weight != null) {
    facts.push({ label: "Default weight", value: `${entry.default_weight}%` });
  }
  if (entry.per_90_of && entry.per_90_minutes_key) {
    // "Per-90 Derivation" is the glossary's term for this (`CONTEXT.md`), so
    // the cell is labelled after the concept rather than after a synonym.
    facts.push({
      label: "Per-90 Derivation",
      value: `${entry.per_90_of} per ninety minutes of ${entry.per_90_minutes_key}`,
    });
  }
  const distribution = entry.distribution ? DISTRIBUTIONS[entry.distribution] : undefined;
  if (distribution) facts.push({ label: "Distribution", value: distribution });
  return facts;
}

/**
 * What the panel prints for one dictionary entry.
 *
 * Pure, and exported so `tests/stat-search.test.ts` can assert the wording
 * under bare `node` — the same reason the rest of this module has no React in
 * it. The component renders this; it decides none of it.
 */
export function statDetail(
  entry: StatEntry,
  use: StatUse = "rank",
  /**
   * What feeds each of the sport's groups, from `labelsFor`.
   *
   * Optional because the menu is useful without it and the query may not have
   * resolved: a group with no entry is printed by name alone rather than with
   * an empty parenthesis, which is the same rule the picker keeps.
   */
  labels: Readonly<Record<string, readonly string[]>> = {},
): StatDetail {
  const ranked = use === "rank";
  return {
    direction: ranked ? directionWords(entry.direction) : null,
    higherIsBetter: entry.direction !== "lower_is_better",
    directionNote: null,
    facts: ranked ? [...identityFacts(entry), ...rankingFacts(entry)] : identityFacts(entry),
    excluded: ranked
      ? entry.excluded_position_groups.map((group) => ({
          group,
          labels: labels[group] ?? [],
        }))
      : [],
    aliases: entry.aliases,
  };
}

/**
 * What the panel prints for a stored key this sport's dictionary does not hold.
 *
 * **It states what will happen to the key rather than what the entry declares**,
 * and getting that backwards is the one way a detail panel can be actively
 * worse than no panel. A Run resolves a Binding by the profile's sport and the
 * key together (ADR-0017), so on a ranked row `runner.py` falls through to
 * `directions.get(key, "higher_is_better")` and the column publishes under the
 * raw key ranking **higher-is-better**, whatever some other sport files it as.
 * Soccer's `penalties` is lower-is-better; a panel built from the entry alone
 * would preview that column exactly wrong, on the one row nobody chose
 * deliberately.
 *
 * **The same fall-through deletes the rest of the ranking facts**, which is why
 * `rankingFacts` is not reached here: the Distribution and the per-90
 * derivation are looked up in that same sport-scoped map and are equally absent.
 * The first cut corrected the Direction and kept the Distribution, which is the
 * identical error one line down.
 *
 * On a **threshold** menu none of that machinery runs at all, and the true
 * consequence is harsher: `_is_eligible` returns `False` for a player with no
 * value under the key, so a participation statistic the sport does not hold
 * excludes every player in the file.
 *
 * What survives either way is the entry itself where it resolved — the key, the
 * sport it came from and its aliases — because that is the statistic the
 * reviewer is looking at, whatever this profile will do with the key.
 */
function offDictionaryDetail(
  key: string,
  entry: StatEntry | null,
  use: StatUse,
): StatDetail {
  const ranked = use === "rank";
  const filed = entry
    ? ` The ${entry.sport} dictionary files it as ${directionWords(entry.direction).toLowerCase()}.`
    : "";

  return {
    direction: ranked ? "Higher is better" : null,
    higherIsBetter: true,
    directionNote: ranked
      ? `Not in this sport's dictionary, so a Run publishes it under the raw key and ranks it higher-is-better.${filed}`
      : "Not in this sport's dictionary, so no player has a value under it and a Run excludes every one of them.",
    // The identity facts only. The sport is left out where nothing resolved the
    // entry — the same refusal the row's own badge makes.
    facts: entry ? identityFacts(entry) : [{ label: "Key", value: key }],
    // **None, even where the entry declares some.** `describe_columns` widens
    // an exclusion onto the keys a Run emitted for *this* sport, and a key this
    // sport's dictionary does not hold resolves to no entry at all — so the
    // other sport's declaration never reaches the column. Printing it would be
    // the same error as printing its Direction, one line up.
    excluded: [],
    aliases: entry?.aliases ?? [],
  };
}

/**
 * What the panel says over the row that stores nothing.
 *
 * Here rather than in the JSX because it is wording, and the wording of this
 * panel is asserted in `tests/` — the rule the rest of this module keeps. The
 * label is the caller's, because holding nothing is an absence on a
 * field-mapping row and a decision (*rank everyone*) on the eligibility rule.
 */
export function clearRowNote(label: string): string {
  return `“${label}” stores no statistic, so there is nothing to preview.`;
}

/** One row of the Statistic menu. */
export interface StatOption {
  /**
   * What choosing this row stores in `Binding.stat_key`. The empty string is
   * the *clear* row, which the cell adds rather than this module — an option
   * that stores nothing is not a statistic and has no business being matched.
   */
  key: string;
  /** Exactly what the row prints. Nothing reformats it downstream. */
  label: string;
  /**
   * Which sport's vocabulary the row comes from, printed as a badge beside the
   * label.
   *
   * Redundant on most rows by construction — every caller scopes `entries` to
   * one sport, so a football profile's menu is 392 rows all badged *football* —
   * and shown anyway, because the rows where it is *not* redundant are the ones
   * that matter: the out-of-sport row a stored `stat_key` adds (the only row in
   * the list from another vocabulary), and a dictionary whose Sport control was
   * moved out from under a bound Binding. A sport is also half of a statistic's
   * identity: `penalties` is flags and lower-is-better in football, penalty
   * kicks and higher-is-better in soccer (ADR-0017), so naming the vocabulary a
   * row was read from says which of the two the reviewer is about to store.
   *
   * Empty where nothing resolves the sport: the *clear* row, and a stored key
   * `AddSource` cannot look up because it loaded one sport's list. Empty prints
   * no badge rather than a blank one.
   *
   * **Not searched**, which is the one thing printed here that is not — see
   * `searchable`.
   */
  sport: string;
  /**
   * A stored key this sport's dictionary does not hold, offered anyway.
   *
   * `StatisticCell`'s header has the argument in full; the short version is
   * that a Binding's `stat_key` is a plain string with no cascade behind it, so
   * a dictionary entry can be deleted — or the **Sport** control moved — out
   * from under a row that is bound. Dropping the row would be the screen
   * claiming the field is unmapped when it is mapped and will be saved mapped.
   */
  offDictionary: boolean;
  /**
   * The normalised text a term is matched against: the label, the key, and each
   * alias.
   *
   * Precomputed with the option rather than derived per keystroke, so typing
   * costs one pass over the list instead of one pass plus the normalisation of
   * every string on it. It is also the honest place for it — the text a row is
   * found by belongs to the row, next to the text it shows.
   */
  search: readonly string[];
  /**
   * The facts the menu's detail panel prints while this row is under the
   * cursor, or null where there is no statistic behind the row — which is the
   * *clear* row, added by the cell rather than here.
   *
   * Carried on the option rather than looked up by key when a row goes active,
   * for the same reason `search` is: the row owns the text it is read by and
   * the text it is read as, and a panel that re-resolved an entry could print
   * one row's facts beside another row's name.
   */
  detail: StatDetail | null;
}

/**
 * Case, spacing and punctuation folded away.
 *
 * The same rule the server's auto-binder uses (`discovery.py:_normalise`), and
 * deliberately the same: `passing_yards`, `Passing Yards` and `passingYards`
 * are one statistic under three spellings, and a reviewer typing any of them
 * means the fourth. Matching them apart here would make the search disagree
 * with the suggestion engine about what a name is.
 */
export function normalise(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * A typed query as the terms it holds.
 *
 * Split rather than normalised whole, so the terms may be matched in any order:
 * "yards passing" finds *Passing Yards*, which a single folded string could
 * not. A query of nothing but spaces and punctuation yields no terms and is
 * therefore not a filter — which is what keeps a stray keystroke from emptying
 * the menu.
 */
export function terms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((term) => term !== "");
}

/**
 * The menu's rows, before any search: the stored value when the dictionary does
 * not hold it, then the dictionary in the order the caller loaded it.
 *
 * The out-of-dictionary row comes first for the reason it always has — the one
 * option nobody chose deliberately is the one worth meeting first — and the
 * ordering survives a search, since matching below is a stable partition.
 *
 * `entries` is already scoped to the profile's sport by the screen that loaded
 * it. That split is deliberate and predates this module: `AddSource` fetches one
 * sport and `ReviewFields` fetches the whole dictionary and filters it, for
 * reasons local to each, and folding the choice in here would make one screen's
 * scope decision the other's.
 */
export function statOptions(
  entries: readonly StatEntry[],
  value: string | null,
  outOfSport: StatEntry | null = null,
  /**
   * What the screen does with the choice. Defaults to `"rank"`, which is both
   * field-mapping screens and therefore every caller but one; `SourceDetail`'s
   * eligibility rule passes `"threshold"` because its choice is compared
   * against a minimum and never ranked.
   */
  use: StatUse = "rank",
  /** What feeds each of the sport's groups, for the detail panel. See
   *  `statDetail`. */
  labels: Readonly<Record<string, readonly string[]>> = {},
): readonly StatOption[] {
  const options: StatOption[] = [];

  const stored = value === null || value === "" ? null : value;
  if (stored !== null && !entries.some((entry) => entry.key === stored)) {
    // Named by the sport it does belong to when that is known, and by its bare
    // key when it is not — `AddSource` loads one sport's list and so cannot
    // resolve another's. Either way the wording is the same claim, and a true
    // one: a Run resolves a Binding by its profile's sport and this key
    // together, so this column publishes under the raw key with the default
    // Direction.
    const label = outOfSport
      ? `${outOfSport.display_name} — ${outOfSport.sport}, not in this sport`
      : `${stored} — not in this sport`;
    options.push({
      key: stored,
      label,
      // Known only where the entry resolved. `AddSource` loads one sport's list,
      // so there the row carries the bare key and no sport to badge it with.
      sport: outOfSport?.sport ?? "",
      offDictionary: true,
      // The entry's own aliases are searched where it is known, so the row is
      // findable by whatever the other sport calls it as well as by the sport's
      // name, which the label prints.
      search: searchable(label, stored, outOfSport?.aliases ?? []),
      detail: offDictionaryDetail(stored, outOfSport, use),
    });
  }

  for (const entry of entries) {
    const label = `${entry.display_name}${entry.direction === "lower_is_better" ? " (lower is better)" : ""}`;
    options.push({
      key: entry.key,
      label,
      sport: entry.sport,
      offDictionary: false,
      search: searchable(label, entry.key, entry.aliases),
      detail: statDetail(entry, use, labels),
    });
  }

  return options;
}

/**
 * The strings one row can be found by.
 *
 * Aliases are searched although they are never shown, and that is the one place
 * this module departs from *what is searched is what is printed*. It earns the
 * departure: an alias is what some source calls the statistic — `QBR` on
 * `passing.QBR` — and the reviewer is reading exactly that name off the **Field**
 * column two cells to the left. Matching it only decides which rows are offered;
 * it binds nothing on its own, which is what separates it from harvesting an
 * alias into the dictionary, a thing ADR-0029 refuses for good reasons.
 *
 * **The sport badge is the departure in the other direction: printed, not
 * searched.** Folding it in would break the filter rather than widen it. Every
 * row in a scoped menu carries the same sport, so `football` in each row's
 * search text makes "fo" a term that is present in all 392 of them — the menu
 * answering a search by showing the whole dictionary — and makes every row
 * *start* with the first term, flattening the two bands `matchStats` ranks on.
 * A term can only be a filter if the rows disagree about it.
 */
function searchable(label: string, key: string, aliases: readonly string[]): readonly string[] {
  return [normalise(label), normalise(key), ...aliases.map(normalise)].filter(
    (text) => text !== "",
  );
}

/**
 * The rows a query matches — **all of them**, in two bands.
 *
 * A row matches when every term is somewhere in it; the terms may land in
 * different strings, so "qbr pass" finds a *Passing* entry aliased `QBR`. No
 * cap and no cut-off: a list that stopped at the tenth match would be a search
 * that hides statistics, which is the problem it was added to solve, and "yards"
 * legitimately matches forty football entries.
 *
 * The two bands are the whole of the ranking. A row one of whose strings
 * *begins* with the first term comes above one that merely contains it, so
 * typing "pass" answers *Passing Yards* before *Completions, Passing*, and
 * dictionary order is preserved inside each band. Nothing is scored beyond
 * that: a fuzzier ranking would reorder a list of forty on every keystroke, and
 * a reviewer who has found their row by eye should not have it move under the
 * cursor because the next character promoted something else.
 */
export function matchStats(
  options: readonly StatOption[],
  query: string,
): readonly StatOption[] {
  const wanted = terms(query);
  // The first term is what the two bands are ranked on, and its absence is what
  // "no search" means — a query of nothing but punctuation reaches here.
  const first = wanted[0];
  if (first === undefined) return options;

  const leading: StatOption[] = [];
  const trailing: StatOption[] = [];
  for (const option of options) {
    const matches = wanted.every((term) => option.search.some((text) => text.includes(term)));
    if (!matches) continue;
    if (option.search.some((text) => text.startsWith(first))) leading.push(option);
    else trailing.push(option);
  }
  return [...leading, ...trailing];
}
